import { Dialog, DialogContent, Button, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SignaturePad from "react-signature-pad-wrapper";
import { v4 as uuid } from "uuid";
import SignaturePadContainer from "./SignaturePadContainer";
import { StyledDialogActions } from "./styles";
import DialogTitle from "../DialogTitle/DialogTitle";
import { Title } from "../DialogTitle/styles";

type MimeType = "image/svg+xml" | "image/png" | "image/jpeg";
type Extension = "svg" | "png" | "jpeg";
interface Props {
  title: string;
  customTitleStyle?: React.CSSProperties;
  text: string;
  buttonTitle: string;
  submitButtonTitle?: string;
  submitButtonStyle?: React.CSSProperties;
  handleSignature?: (signature: File, type: string) => void;
  children?: any;
  openFromTheStart?: boolean;
  additionalActions?: JSX.Element;
  wrapInDialog?: boolean;
  handleCancel?: () => void;
  alertWhenEmpty?: string;
  disabled?: boolean;
  customMimeTypeAndExtension?: {
    mimeType: MimeType;
    extension: Extension;
  };
  displayOnlyClearButton?: boolean;
  externalCanvasRef?: React.RefObject<SignaturePad>;
  customClearButtonStyle?: React.CSSProperties;
  minPencilWidth?: number;
  maxPencilWidth?: number;
}
interface State {
  open: boolean;
}

const SignaturePadWrapper = ({
  title,
  customTitleStyle,
  text,
  buttonTitle,
  submitButtonTitle,
  submitButtonStyle,
  handleSignature,
  children,
  openFromTheStart,
  additionalActions,
  wrapInDialog,
  handleCancel,
  alertWhenEmpty,
  disabled = false,
  customMimeTypeAndExtension,
  displayOnlyClearButton,
  externalCanvasRef,
  customClearButtonStyle,
  minPencilWidth,
  maxPencilWidth,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const sigCanvas = useRef<SignaturePad>(null);
  const tRP = "components.signature_pad";
  const [state, setState] = useState<State>({
    open: openFromTheStart ?? false,
  });

  // #region submit
  const submit = useCallback(async () => {
    if (sigCanvas.current?.isEmpty()) {
      alert(alertWhenEmpty ?? "Please draw your signature");
    } else {
      if (sigCanvas.current) {
        const blob = await fetch(
          sigCanvas.current.toDataURL(customMimeTypeAndExtension?.mimeType ?? "image/svg+xml") ?? ""
        ).then((r) => r.blob());

        handleCancel
          ? handleCancel()
          : setState((state) => ({
              ...state,
              open: false,
            }));

        handleSignature &&
          handleSignature(
            new File([blob], `sig_${uuid()}.${customMimeTypeAndExtension?.extension ?? "svg"}`),
            customMimeTypeAndExtension?.mimeType ?? "image/svg+xml"
          );
      }
    }
  }, [handleSignature, customMimeTypeAndExtension, handleCancel]);
  // #endregion

  // #region clear
  const clear = useCallback(() => {
    externalCanvasRef?.current ? externalCanvasRef.current.clear() : !!sigCanvas.current && sigCanvas.current.clear();
  }, [externalCanvasRef]);
  // #endregion

  // #region cancel
  const cancel = useCallback(async () => {
    setState((state) => ({
      ...state,
      open: false,
    }));
  }, []);
  // #endregion

  return (
    <>
      {wrapInDialog ? (
        <>
          {children ? (
            <div onClick={() => !disabled && setState((state) => ({ ...state, open: true }))}>{children}</div>
          ) : (
            <Button
              title={buttonTitle}
              variant="contained"
              onClick={() => !disabled && setState((state) => ({ ...state, open: true }))}
            >
              <Typography>{buttonTitle}</Typography>
            </Button>
          )}
          <Dialog open={state.open}>
            <DialogTitle
              title={title}
              handleClose={() => {
                setState((state) => ({ ...state, open: false }));
              }}
            />
            <DialogContent>
              {additionalActions}
              <SignaturePadContainer
                text={text}
                refOBj={externalCanvasRef ?? sigCanvas}
                minPencilWidth={minPencilWidth}
                maxPencilWidth={maxPencilWidth}
              />
            </DialogContent>
            <StyledDialogActions>
              {!displayOnlyClearButton && <Button onClick={handleCancel}>{t("common_words.cancel")}</Button>}
              <Button
                onClick={clear}
                style={{ ...customClearButtonStyle }}
                className="siganture-pad-clear-button"
              >{`${t(`${tRP}.clear`)}`}</Button>
              {additionalActions}
              {!displayOnlyClearButton && (
                <Button
                  onClick={submit}
                  variant={"contained"}
                  style={{
                    ...submitButtonStyle,
                  }}
                >
                  {submitButtonTitle ?? `${t("buttons.submit")}`}
                </Button>
              )}
            </StyledDialogActions>
          </Dialog>
        </>
      ) : (
        <Grid style={{ display: "flex", flexDirection: "column" }}>
          <Title style={{ padding: "0px", ...customTitleStyle }}>{title}</Title>

          <SignaturePadContainer
            text={text}
            refOBj={externalCanvasRef ?? sigCanvas}
            minPencilWidth={minPencilWidth}
            maxPencilWidth={maxPencilWidth}
          />
          <Grid
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {!displayOnlyClearButton && <Button onClick={handleCancel}>{t("common_words.cancel")}</Button>}
            <Button
              onClick={clear}
              style={{ ...customClearButtonStyle }}
              className="siganture-pad-clear-button"
            >{`${t(`${tRP}.clear`)}`}</Button>
            {additionalActions}
            {!displayOnlyClearButton && (
              <Button
                onClick={submit}
                variant={"contained"}
                style={{
                  ...submitButtonStyle,
                }}
              >
                {submitButtonTitle ?? `${t("buttons.submit")}`}
              </Button>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};
export default SignaturePadWrapper;
